import { useState } from 'react';
import { C, FONTS } from '../../tokens/design';
import { useClient } from '../../context/ClientContext';
import { useUser } from '../../context/UserContext';
import { useApiCall } from '../../hooks/useApi';
import { useApiClient } from '../../api/apiClient';
import Shell from '../../components/layout/Shell';
import Card, { CardHeader } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Skeleton from '../../components/ui/Skeleton';

const SECRET_TYPES = [
  { value: 'aws-access-key', label: 'AWS Access Key' },
  { value: 'aws-secret-key', label: 'AWS Secret Key' },
  { value: 'ad-service-password', label: 'AD Service Account Password' },
];

export default function ClientSecrets() {
  const { clientId } = useClient();
  const { isPlatformAdmin, isMspUser } = useUser();
  const canWrite = isPlatformAdmin || isMspUser;
  const api = useApiClient();
  const { data: secrets, loading, refetch } = useApiCall(clientId ? `/clients/${clientId}/secrets` : null);
  const items = secrets ?? [];

  const [secretType, setSecretType] = useState('');
  const [secretValue, setSecretValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const handleSave = async () => {
    if (!secretType || !secretValue) return;
    setSaving(true);
    setResult(null);
    try {
      await api.post(`/clients/${clientId}/secrets`, {
        secretName: secretType,
        secretValue,
      });
      setResult({ ok: true, msg: 'Secret stored successfully.' });
      setSecretType('');
      setSecretValue('');
      if (refetch) refetch();
    } catch (err) {
      setResult({ ok: false, msg: `Failed: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Shell title="Client Secrets" sub="Manage stored credentials for scan integrations">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Card style={{ padding: 20 }}>
          <CardHeader title="Stored Secrets" action={<span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>{items.length} secrets</span>} />
          {loading && [0, 1].map(i => (
            <div key={i} style={{ padding: '10px 0', borderBottom: `1px solid ${C.b2}`, display: 'flex', alignItems: 'center', gap: 12 }}>
              <Skeleton width={160} height={14} />
              <Skeleton width={80} height={12} />
            </div>
          ))}
          {!loading && items.length === 0 && (
            <div style={{ padding: '20px 0', textAlign: 'center', fontSize: 11, color: C.t3, fontFamily: FONTS.mono }}>
              No secrets stored yet. Add a secret below to enable scan integrations.
            </div>
          )}
          {items.map((s, i) => {
            const name = s.secretName ?? s.name ?? s.key ?? '';
            const label = (Array.isArray(SECRET_TYPES) ? SECRET_TYPES : []).find(t => t.value === name)?.label ?? name;
            const createdAt = s.createdAt ?? s.updatedAt ?? null;
            return (
              <div key={i} style={{ padding: '10px 0', borderBottom: `1px solid ${C.b2}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, fontFamily: FONTS.sans }}>{label}</div>
                  <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, marginTop: 2 }}>{name}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, letterSpacing: '0.15em' }}>{'\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}</span>
                  {createdAt && <span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>{new Date(createdAt).toLocaleDateString()}</span>}
                </div>
              </div>
            );
          })}
        </Card>

        <Card style={{ padding: 20 }}>
          <CardHeader title="Add Secret" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 14, marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>Secret Type</div>
              <select value={secretType} onChange={e => setSecretType(e.target.value)} style={{ width: '100%', background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6, padding: '9px 11px', color: C.t1, fontSize: 12, fontFamily: FONTS.mono, outline: 'none', cursor: 'pointer' }}>
                <option value="">— Select —</option>
                {SECRET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>Secret Value</div>
              <input type="password" value={secretValue} onChange={e => setSecretValue(e.target.value)} placeholder="Enter secret value..." style={{ width: '100%', background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6, padding: '9px 11px', color: C.t1, fontSize: 12, fontFamily: FONTS.mono, outline: 'none' }} />
            </div>
          </div>

          {result && (
            <div style={{ marginBottom: 14, padding: '8px 12px', borderRadius: 6, fontSize: 11, fontFamily: FONTS.mono, background: result.ok ? `${C.grn}12` : C.redBg, border: `1px solid ${result.ok ? C.grn : C.red}33`, color: result.ok ? C.grn : C.red }}>
              {result.msg}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="pri" onClick={handleSave} disabled={!secretType || !secretValue || saving || !canWrite}>
              {saving ? 'Saving...' : canWrite ? 'Store Secret' : 'Read Only'}
            </Button>
          </div>

          <div style={{ marginTop: 14, padding: 12, background: C.s2, borderRadius: 6, border: `1px solid ${C.b1}` }}>
            <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, lineHeight: 1.6 }}>
              Secrets are encrypted at rest and only used by the scan engine. Values are never displayed after storage.
            </div>
          </div>
        </Card>
      </div>
    </Shell>
  );
}
